import { createRootRoute, createRoute, createRouter, Navigate, Outlet, useRouterState, type RouterHistory } from "@tanstack/react-router";
import { Building2 } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AppLayout } from "@/components/layout";
import { applyAccountLanguage } from "@/i18n";
import { EmptyState, Spinner } from "@/components/ui";
import { useAuthStatus, useMe } from "@/lib/queries";
import { isInstanceAdmin, isOrgAdmin } from "@/lib/roles";
import { AdminCredentialsPage, AdminDevicesPage, AdminGitPage, AdminLlmPage, AdminTagsPage, AdminUsersPage, LicensePage, OrganizationPage } from "./routes/admin";
import { PublicAppPage, PublicBuildPage } from "./routes/public";
import { AppSettingsPage } from "./routes/app-settings";
import { AuditPage } from "./routes/audit";
import { BillingPage } from "./routes/billing";
import { AppPage, AppsPage } from "./routes/apps";
import { LoginPage, SetupPage, SignupPage, safeRedirect } from "./routes/auth";
import { InvitationPage } from "./routes/invitation";
import { ForgotPasswordPage, ResetPasswordPage } from "./routes/password";
import { BuildPage } from "./routes/build";
import { SettingsPage } from "./routes/settings";

function FullPageSpinner() {
  return (
    <div className="flex h-full items-center justify-center">
      <Spinner className="h-6 w-6" />
    </div>
  );
}

const PUBLIC = ["/login", "/signup", "/setup", "/forgot-password"];
const isPublicPage = (pathname: string) => pathname.startsWith("/p/") || pathname.startsWith("/invitations/") || pathname === "/reset-password";

/** Public pages render without looking at the session; everything else goes through AuthGate. */
function Root() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return isPublicPage(pathname) ? <Outlet /> : <AuthGate pathname={pathname} />;
}

/** Redirects according to instance initialization and session state. */
function AuthGate({ pathname }: { pathname: string }) {
  const search = useRouterState({ select: (s) => s.location.search }) as { redirect?: string };
  const status = useAuthStatus();
  const me = useMe();
  if (status.isLoading || me.isLoading) return <FullPageSpinner />;
  if (status.data && !status.data.initialized) return pathname === "/setup" ? <Outlet /> : <Navigate to="/setup" />;
  if (!me.data) {
    if (pathname === "/signup" && !status.data?.signupEnabled) return <Navigate to="/login" />;
    return PUBLIC.includes(pathname) && pathname !== "/setup" ? <Outlet /> : <Navigate to="/login" />;
  }
  if (PUBLIC.includes(pathname)) return <Navigate to={safeRedirect(search.redirect) ?? "/apps"} />;
  return <Outlet />;
}

function Authed() {
  const me = useMe();
  useEffect(() => applyAccountLanguage(me.data?.locale), [me.data?.locale]);
  if (!me.data) return <FullPageSpinner />;
  return <AppLayout me={me.data}>{me.data.organization ? undefined : <NoOrganization />}</AppLayout>;
}

/** Owners and admins of the active organization. */
function AdminOnly({ children }: { children: React.ReactNode }) {
  const me = useMe();
  if (!isOrgAdmin(me.data)) return <Navigate to="/apps" />;
  return <>{children}</>;
}

function InstanceAdminOnly({ children }: { children: React.ReactNode }) {
  const me = useMe();
  if (!isInstanceAdmin(me.data)) return <Navigate to="/apps" />;
  return <>{children}</>;
}

/** Signed-in users outside of any organization (removed from their last one). */
function NoOrganization() {
  const { t } = useTranslation();
  return <EmptyState icon={<Building2 className="h-8 w-8" />} title={t("org.none")}>{t("org.noneHelp")}</EmptyState>;
}

const rootRoute = createRootRoute({ component: Root });
const setupRoute = createRoute({ getParentRoute: () => rootRoute, path: "/setup", component: SetupPage });
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: LoginPage });
const signupRoute = createRoute({ getParentRoute: () => rootRoute, path: "/signup", component: SignupPage });
const forgotPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: "/forgot-password", component: ForgotPasswordPage });
const resetPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: "/reset-password", component: ResetPasswordPage });
const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invitations/$id",
  component: function InvitationRoute() {
    const { id } = invitationRoute.useParams();
    return <InvitationPage id={id} />;
  },
});
const publicAppRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/p/$token",
  component: function PublicAppRoute() {
    const { token } = publicAppRoute.useParams();
    return <PublicAppPage token={token} />;
  },
});
const publicBuildRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/p/$token/b/$buildId",
  component: function PublicBuildRoute() {
    const { token, buildId } = publicBuildRoute.useParams();
    return <PublicBuildPage token={token} buildId={buildId} />;
  },
});
const authedRoute = createRoute({ getParentRoute: () => rootRoute, id: "authed", component: Authed });

const indexRoute = createRoute({ getParentRoute: () => authedRoute, path: "/", component: () => <Navigate to="/apps" /> });
const appsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/apps",
  component: AppsPage,
  validateSearch: (search: Record<string, unknown>): { tag?: string } => (typeof search.tag === "string" ? { tag: search.tag } : {}),
});
const newAppRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/apps/new",
  component: () => (
    <AdminOnly>
      <AppSettingsPage />
    </AdminOnly>
  ),
});
const appRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/apps/$appId",
  component: function AppRoute() {
    const { appId } = appRoute.useParams();
    return <AppPage key={appId} appId={appId} />;
  },
});
const appSettingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/apps/$appId/settings",
  component: function AppSettingsRoute() {
    const { appId } = appSettingsRoute.useParams();
    return (
      <AdminOnly>
        <AppSettingsPage key={appId} appId={appId} />
      </AdminOnly>
    );
  },
});
const buildRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "/apps/$appId/builds/$buildId",
  component: function BuildRoute() {
    const { appId, buildId } = buildRoute.useParams();
    return <BuildPage key={buildId} appId={appId} buildId={buildId} />;
  },
});
const settingsRoute = createRoute({ getParentRoute: () => authedRoute, path: "/settings", component: SettingsPage });
const admin = <TPath extends string>(path: TPath, Page: () => React.ReactNode) =>
  createRoute({
    getParentRoute: () => authedRoute,
    path,
    component: () => (
      <AdminOnly>
        <Page />
      </AdminOnly>
    ),
  });

const instanceAdmin = <TPath extends string>(path: TPath, Page: () => React.ReactNode) =>
  createRoute({
    getParentRoute: () => authedRoute,
    path,
    component: () => (
      <InstanceAdminOnly>
        <Page />
      </InstanceAdminOnly>
    ),
  });

const routeTree = rootRoute.addChildren([
  setupRoute,
  loginRoute,
  signupRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  invitationRoute,
  publicAppRoute,
  publicBuildRoute,
  authedRoute.addChildren([
    indexRoute,
    appsRoute,
    newAppRoute,
    appRoute,
    appSettingsRoute,
    buildRoute,
    settingsRoute,
    admin("/organization", OrganizationPage),
    admin("/organization/billing", BillingPage),
    admin("/organization/audit", AuditPage),
    instanceAdmin("/admin/users", AdminUsersPage),
    instanceAdmin("/admin/license", LicensePage),
    admin("/admin/credentials", AdminCredentialsPage),
    admin("/admin/llm", AdminLlmPage),
    admin("/admin/git", AdminGitPage),
    admin("/admin/tags", AdminTagsPage),
    admin("/admin/devices", AdminDevicesPage),
  ]),
]);

export const createAppRouter = (options: { history?: RouterHistory } = {}) => createRouter({ routeTree, defaultPreload: "intent", ...options });

export const router = createAppRouter();

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
